import { useState, useEffect, useMemo } from 'react'
import { Modal, Form, Button, Table, Alert, Row, Col, Spinner, Badge } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'
import { formatCurrency } from '../../utils/formatters'
import { extractApiErrorMessage } from '../../utils/apiErrors'

const MOTIVOS = [
  { value: 'descuento', label: 'Descuento' },
  { value: 'bonificacion', label: 'Bonificación' },
  { value: 'devolucion', label: 'Devolución' },
  { value: 'ajuste', label: 'Ajuste' },
  { value: 'saldo_a_favor', label: 'Saldo a favor' },
]

const TIPOS_RELACION_E = [
  { value: '01', label: '01 - Nota de crédito' },
  { value: '02', label: '02 - Nota de débito' },
  { value: '03', label: '03 - Devolución de mercancía' },
  { value: '04', label: '04 - Sustitución de CFDI previos' },
  { value: '07', label: '07 - CFDI por aplicación de anticipo' },
]

const MOTIVO_TIPO_REL = {
  descuento: '01',
  bonificacion: '01',
  devolucion: '03',
  ajuste: '01',
  saldo_a_favor: '01',
}

const NotaCreditoModal = ({ show, onHide, clients = [], onCreated }) => {
  const queryClient = useQueryClient()
  const [clientId, setClientId] = useState('')
  const [facturaId, setFacturaId] = useState('')
  const [motivo, setMotivo] = useState('descuento')
  const [tipoRelacion, setTipoRelacion] = useState('01')
  const [modoMonto, setModoMonto] = useState('importe') // importe | porcentaje
  const [importe, setImporte] = useState('')
  const [porcentaje, setPorcentaje] = useState('')
  const [autorizacionEspecial, setAutorizacionEspecial] = useState(false)
  const [calculo, setCalculo] = useState(null)

  useEffect(() => {
    if (!show) {
      setClientId('')
      setFacturaId('')
      setMotivo('descuento')
      setTipoRelacion('01')
      setModoMonto('importe')
      setImporte('')
      setPorcentaje('')
      setAutorizacionEspecial(false)
      setCalculo(null)
    }
  }, [show])

  useEffect(() => {
    setTipoRelacion(MOTIVO_TIPO_REL[motivo] || '01')
    setCalculo(null)
  }, [motivo])

  useEffect(() => {
    setFacturaId('')
    setCalculo(null)
  }, [clientId])

  useEffect(() => {
    setCalculo(null)
  }, [facturaId, importe, porcentaje, modoMonto, tipoRelacion, autorizacionEspecial])

  const { data: facturasData, isLoading: loadingFacturas } = useQuery(
    ['facturas-para-nc', clientId],
    () => api.get(`/financial/invoices/facturas-para-nota-credito/?client_id=${clientId}`).then((r) => r.data),
    { enabled: !!show && !!clientId }
  )

  // useMemo: mismo motivo que en ComplementoPagoModal.
  const facturas = useMemo(() => facturasData?.results || [], [facturasData])
  const facturaSel = useMemo(
    () => facturas.find((f) => String(f.id) === String(facturaId)),
    [facturas, facturaId]
  )

  const { data: resumen, isLoading: loadingResumen } = useQuery(
    ['resumen-nc', facturaId],
    () => api.get(`/financial/invoices/${facturaId}/resumen-nota-credito/`).then((r) => r.data),
    { enabled: !!show && !!facturaId }
  )

  const panel = resumen || facturaSel

  const calcularMutation = useMutation(
    async () => {
      const body = {
        motivo,
        tipo_relacion: tipoRelacion,
        autorizacion_especial: autorizacionEspecial,
      }
      if (modoMonto === 'porcentaje') body.porcentaje = porcentaje
      else body.monto = importe
      const res = await api.post(`/financial/invoices/${facturaId}/calcular-nota-credito/`, body)
      return res.data
    },
    {
      onSuccess: (data) => {
        setCalculo(data)
        toast.success('Cálculo de NC listo')
      },
      onError: (err) => toast.error(extractApiErrorMessage(err, 'No se pudo calcular la NC')),
    }
  )

  const crearMutation = useMutation(
    async () => {
      const body = {
        factura_origen_id: parseInt(facturaId, 10),
        motivo,
        tipo_relacion: tipoRelacion,
        autorizacion_especial: autorizacionEspecial,
      }
      if (modoMonto === 'porcentaje') body.porcentaje = porcentaje
      else body.monto = importe
      const res = await api.post('/financial/invoices/crear-nota-credito/', body)
      return res.data
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('invoices')
        toast.success('Nota de crédito creada en borrador')
        onCreated?.(data.factura)
        onHide()
      },
      onError: (err) => toast.error(extractApiErrorMessage(err, 'No se pudo crear la NC')),
    }
  )

  const saldoDisp = parseFloat(panel?.saldo_disponible || 0)
  const canCalcular =
    facturaId &&
    (modoMonto === 'porcentaje'
      ? parseFloat(porcentaje) > 0 && (saldoDisp > 0 || autorizacionEspecial)
      : parseFloat(importe) > 0)

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Crear Nota de Crédito</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="py-2">
          <small>
            CFDI tipo E (egreso). No cancela la factura. El porcentaje se calcula sobre el{' '}
            <strong>saldo disponible</strong> (total − pagos − NC previas). Puede ser parcial o total.
          </small>
        </Alert>

        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Cliente (emisor) *</Form.Label>
              <Form.Select value={clientId} onChange={(e) => setClientId(e.target.value)}>
                <option value="">Seleccionar…</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.razon_social || c.business_name || `${c.nombre || ''} ${c.apellido_paterno || ''}`.trim() || c.rfc}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Factura vigente *</Form.Label>
              <Form.Select
                value={facturaId}
                onChange={(e) => setFacturaId(e.target.value)}
                disabled={!clientId || loadingFacturas}
              >
                <option value="">{loadingFacturas ? 'Cargando…' : 'Seleccionar factura…'}</option>
                {facturas.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.invoice_number} — saldo {formatCurrency(f.saldo_disponible)} — {f.metodo_pago}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {facturaId && (
          <div className="mb-3 border rounded p-3 bg-light">
            {loadingResumen && !panel ? (
              <Spinner size="sm" />
            ) : panel ? (
              <>
                <h6 className="mb-2">
                  Factura {panel.invoice_number}{' '}
                  <Badge bg="secondary">{panel.metodo_pago}</Badge>
                </h6>
                <Table size="sm" borderless className="mb-2">
                  <tbody>
                    <tr>
                      <td>Total factura</td>
                      <td className="text-end">{formatCurrency(panel.total)}</td>
                    </tr>
                    <tr>
                      <td>Pagos aplicados</td>
                      <td className="text-end">{formatCurrency(panel.pagos_aplicados)}</td>
                    </tr>
                    <tr>
                      <td>NC previas</td>
                      <td className="text-end">{formatCurrency(panel.nc_previas)}</td>
                    </tr>
                    <tr>
                      <td><strong>Saldo disponible</strong></td>
                      <td className="text-end"><strong>{formatCurrency(panel.saldo_disponible)}</strong></td>
                    </tr>
                    <tr>
                      <td>Tope económico NC</td>
                      <td className="text-end">{formatCurrency(panel.tope_economico_nc)}</td>
                    </tr>
                    <tr>
                      <td>Receptor</td>
                      <td className="text-end small">{panel.receptor_rfc} — {panel.receptor_nombre}</td>
                    </tr>
                  </tbody>
                </Table>
                {panel.reps_relacionados?.length > 0 && (
                  <>
                    <div className="small fw-bold">REP / complementos relacionados</div>
                    <ul className="small mb-0">
                      {panel.reps_relacionados.map((r) => (
                        <li key={r.aplicacion_id}>
                          {r.complemento_folio}
                          {r.complemento_uuid ? ` (${r.complemento_uuid.slice(0, 8)}…)` : ' sin timbrar'}
                          {' — '}{formatCurrency(r.monto)}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {(!panel.reps_relacionados || panel.reps_relacionados.length === 0) && (
                  <div className="small text-muted">Sin REP relacionados.</div>
                )}
              </>
            ) : null}
          </div>
        )}

        <Row className="mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Motivo *</Form.Label>
              <Form.Select value={motivo} onChange={(e) => setMotivo(e.target.value)}>
                {MOTIVOS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Tipo relación SAT *</Form.Label>
              <Form.Select value={tipoRelacion} onChange={(e) => setTipoRelacion(e.target.value)}>
                {TIPOS_RELACION_E.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Captura *</Form.Label>
              <Form.Select value={modoMonto} onChange={(e) => setModoMonto(e.target.value)}>
                <option value="importe">Importe</option>
                <option value="porcentaje">Porcentaje sobre saldo disponible</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Form.Check
          className="mb-3"
          type="checkbox"
          id="auth-especial-nc"
          label="Autorización especial (permitir exceder saldo/tope; el excedente genera saldo a favor)"
          checked={autorizacionEspecial}
          onChange={(e) => setAutorizacionEspecial(e.target.checked)}
        />

        <Row className="mb-3">
          <Col md={6}>
            {modoMonto === 'importe' ? (
              <Form.Group>
                <Form.Label>Importe NC *</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={importe}
                  onChange={(e) => setImporte(e.target.value)}
                  placeholder={saldoDisp > 0 ? `Máx. saldo ${saldoDisp}` : 'Importe (saldo a favor si pagada)'}
                />
              </Form.Group>
            ) : (
              <Form.Group>
                <Form.Label>Porcentaje sobre saldo disponible *</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={porcentaje}
                  onChange={(e) => setPorcentaje(e.target.value)}
                  disabled={saldoDisp <= 0 && !autorizacionEspecial}
                />
                {saldoDisp <= 0 && !autorizacionEspecial && (
                  <Form.Text className="text-danger">
                    Sin saldo disponible: use importe o active autorización especial.
                  </Form.Text>
                )}
              </Form.Group>
            )}
          </Col>
        </Row>

        {calculo && (
          <Alert variant="success" className="py-2">
            <div><strong>Total NC:</strong> {formatCurrency(calculo.total_nc)}</div>
            <div className="small">
              Base: {formatCurrency(calculo.base)} · IVA: {formatCurrency(calculo.iva)} · Retenciones:{' '}
              {formatCurrency(calculo.retenciones)}
            </div>
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancelar</Button>
        <Button
          variant="outline-primary"
          disabled={!canCalcular || calcularMutation.isLoading}
          onClick={() => calcularMutation.mutate()}
        >
          {calcularMutation.isLoading ? 'Calculando…' : 'Calcular'}
        </Button>
        <Button
          variant="primary"
          disabled={!calculo || crearMutation.isLoading}
          onClick={() => crearMutation.mutate()}
        >
          {crearMutation.isLoading ? 'Creando…' : 'Generar borrador NC'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default NotaCreditoModal
